// src/components/Layout/Layout.jsx
import React, { useState, useEffect } from "react";
import Navbar from "../Navbar/Navbar";
import { Outlet, useLocation } from "react-router-dom";
import "./Layout.css";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../api/axios";
import { clearToken } from "../../utils/authToken";
import ModernSidebar from "../Sidebare/ModernSidebar";
import MobileNavbar from "../MobileNavbar/MobileNavbar";

const BASE_URL = "https://farm-project-bbzj.onrender.com";

export default function Layout() {
  const location = useLocation();
  const { i18n } = useTranslation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebarOpen");
    return saved ? saved === "1" : false;
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isRTL = i18n.language === "ar" || i18n.language === "ur";
  const [isFattening] = useState(false);

  const hideSidebarPaths = [
    "/",
    "/home",
    "/login",
    "/register",
    "/forgetpassword",
    "/verifyotp",
    "/resetpassword",
    "/listPlan",
  ];

  const { data: notifCheckData } = useQuery({
    queryKey: ["notifications", "check", i18n.language],
    queryFn: async () => {
      const lang = i18n.language || "ar";
      const res = await axiosInstance.get(`${BASE_URL}/api/notifications/check`, {
        params: { lang },
      });

      // متوقع يرجع حاجة زي:
      // { status: "success", data: { unreadCount, hasNew, ... } }
      return res.data?.data || { unreadCount: 0, hasNew: false };
    },
    refetchInterval: 60000, // كل 60 ثانية يشيك تاني (اختياري)
  });

  const unreadCount = notifCheckData?.unreadCount ?? 0;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarOpen", isSidebarOpen ? "1" : "0");
  }, [isSidebarOpen]);

  useEffect(() => {
    // Ensure language is set correctly on mount and route changes
    const savedLang = localStorage.getItem('lang');
    const lang = savedLang && ['en', 'ar', 'ur'].includes(savedLang) ? savedLang : 'ar';
    
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    
    const dir = lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [location.pathname, i18n]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const isCourseStudentArea =
    location.pathname === "/course" || location.pathname.startsWith("/course/");
  const shouldShowSidebar =
    !hideSidebarPaths.includes(location.pathname) && !isCourseStudentArea;
  const shouldShowNavbar =
    location.pathname === "/" || location.pathname === "/home";

  return (
    <div
      className={`app-container ${
        isSidebarOpen && shouldShowSidebar ? "app-has-wide-sidebar" : ""
      }`}
    >
      {/* ✨ Navbar العادي يظهر بس في المسارات المحددة */}
      {shouldShowNavbar && !isMobile && (
        <Navbar
          toggleSidebar={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
          showSidebarToggle={shouldShowSidebar}
        />
      )}

      {/* ✨ Navbar خاص بالموبايل */}
      {isMobile && shouldShowSidebar && (
        <MobileNavbar
          toggleSidebar={toggleSidebar}
          notificationCount={unreadCount}
        />
      )}

      <div
        className={`content-wrapper ${isRTL ? "rtl" : "ltr"} ${
          shouldShowSidebar ? "should-show-sidebar" : "no-sidebar"
        }`}
      >
        {shouldShowSidebar && (
          <>
            {/* Sidebar */}
            <ModernSidebar
              isOpen={isSidebarOpen}
              isMobile={isMobile}
              isRTL={isRTL}
              notificationCount={unreadCount}
              isFattening={isFattening}
              onLogout={() => {
                clearToken();
                window.location.href = "/";
              }}
              onChangeLanguage={(lang) => {
                i18n.changeLanguage(lang);
                localStorage.setItem("lang", lang);
                document.dir = lang === "ar" || lang === "ur" ? "rtl" : "ltr";
                // Update HTML lang attribute for SEO
                document.documentElement.lang = lang;
              }}
              onToggle={toggleSidebar}
            />

            {/* scrim في الموبايل */}
            {isSidebarOpen && isMobile && (
              <div className="sidebar-scrim" onClick={toggleSidebar} />
            )}
          </>
        )}

        <main
          className={`main-content ${
            shouldShowSidebar && isSidebarOpen ? "with-sidebar" : "full-width"
          }`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}